import { useCallback, useMemo, useState } from "react";
import { SpaceMap } from "types/SpaceMap";
import { SpaceType } from "types/SpaceType";

const useSpaces = () => {
	const [activeSpaces, setActiveSpaces] = useState<{
		[key: string]: string | null;
	}>({
		"personal-spaces": null,
		barriers: null,
		healthcare: null,
		"food-nutrition": null,
		education: null,
		community: null,
		transportation: null,
		housing: null,
		economic: null,
	});

	const healthcareSpaces: SpaceMap = useMemo(
		() => ({
			hospital: { label: "Hospital", icon: "🏥", color: "#f44336" },
			primaryCare: { label: "Primary Care", icon: "👨‍⚕️", color: "#2196F3" },
			mentalHealth: { label: "Mental Health", icon: "🧠", color: "#9C27B0" },
			pharmacy: { label: "Pharmacy", icon: "💊", color: "#4CAF50" },
			dentalCare: { label: "Dental Care", icon: "🦷", color: "#FF9800" },
			visionCare: { label: "Vision Care", icon: "👁️", color: "#03A9F4" },
			emergencyCare: { label: "Emergency Care", icon: "🚑", color: "#f44336" },
		}),
		[]
	);

	const foodSpaces: SpaceMap = useMemo(
		() => ({
			grocery: { label: "Grocery Store", icon: "🏪", color: "#2196F3" },
			restaurant: { label: "Restaurant", icon: "🍽️", color: "#FF9800" },
			foodBank: { label: "Food Bank", icon: "🥫", color: "#4CAF50" },
			farmersMarket: { label: "Farmers Market", icon: "🌾", color: "#8BC34A" },
		}),
		[]
	);

	const educationSpaces: SpaceMap = useMemo(
		() => ({
			school: { label: "School", icon: "🏫", color: "#2196F3" },
			library: { label: "Library", icon: "📚", color: "#9C27B0" },
			tutoring: { label: "Tutoring Center", icon: "✏️", color: "#4CAF50" },
			skillsTraining: {
				label: "Skills Training",
				icon: "🛠️",
				color: "#FF9800",
			},
		}),
		[]
	);

	const communitySpaces: SpaceMap = useMemo(
		() => ({
			communityCenter: {
				label: "Community Center",
				icon: "🏛️",
				color: "#4CAF50",
			},
			seniorCenter: { label: "Senior Center", icon: "👴", color: "#FF9800" },
			youthCenter: { label: "Youth Center", icon: "🧑", color: "#2196F3" },
			supportGroup: { label: "Support Group", icon: "🤝", color: "#9C27B0" },
			legalAid: { label: "Legal Aid", icon: "⚖️", color: "#795548" },
			financialServices: {
				label: "Financial Services",
				icon: "💰",
				color: "#FF5722",
			},
			translationServices: {
				label: "Translation Services",
				icon: "🗣️",
				color: "#607D8B",
			},
		}),
		[]
	);

	const transportationSpaces: SpaceMap = useMemo(
		() => ({
			busStop: { label: "Bus Stop", icon: "🚌", color: "#2196F3" },
			trainStation: { label: "Train Station", icon: "🚉", color: "#1976D2" },
			bikeShare: { label: "Bike Share", icon: "🚲", color: "#4CAF50" },
			accessibleTransit: {
				label: "Accessible Transit",
				icon: "♿",
				color: "#9C27B0",
			},
			medicalTransport: {
				label: "Medical Transport",
				icon: "🚑",
				color: "#f44336",
			},
			walkingPath: { label: "Walking Path", icon: "🚶", color: "#8BC34A" },
			rideshareHub: { label: "Rideshare Hub", icon: "🚗", color: "#FF9800" },
		}),
		[]
	);

	const housingSpaces: SpaceMap = useMemo(
		() => ({
			affordableHousing: {
				label: "Affordable Housing",
				icon: "🏘️",
				color: "#2196F3",
			},
			shelter: { label: "Shelter", icon: "🏠", color: "#4CAF50" },
			housingAssistance: {
				label: "Housing Assistance",
				icon: "🔑",
				color: "#FF9800",
			},
			utilityAssistance: {
				label: "Utility Assistance",
				icon: "⚡",
				color: "#FFC107",
			},
			homeRepair: { label: "Home Repair", icon: "🔧", color: "#795548" },
			seniorHousing: { label: "Senior Housing", icon: "👴", color: "#9C27B0" },
			familyHousing: { label: "Family Housing", icon: "👨‍👩‍👧‍👦", color: "#E91E63" },
			transitionalHousing: {
				label: "Transitional Housing",
				icon: "🏡",
				color: "#3F51B5",
			},
		}),
		[]
	);

	const economicSpaces: SpaceMap = useMemo(
		() => ({
			jobCenter: { label: "Job Center", icon: "💼", color: "#FFD700" },
			smallBusiness: {
				label: "Small Business Support",
				icon: "🏪",
				color: "#DAA520",
			},
			financialEd: {
				label: "Financial Education",
				icon: "📊",
				color: "#B8860B",
			},
			creditUnion: { label: "Credit Union", icon: "🏦", color: "#BDB76B" },
			careerTraining: {
				label: "Career Training",
				icon: "📚",
				color: "#F0E68C",
			},
			workforce: {
				label: "Workforce Development",
				icon: "👥",
				color: "#EEE8AA",
			},
			entrepreneur: {
				label: "Entrepreneurship Support",
				icon: "🚀",
				color: "#FAFAD2",
			},
		}),
		[]
	);

	const personalSpaces: SpaceMap = useMemo(
		() => ({
			home: { label: "Home", icon: "🏠", color: "#4CAF50" },
			work: { label: "Work", icon: "💼", color: "#2196F3" },
			school: { label: "School", icon: "🎓", color: "#FFC107" },
			gym: { label: "Gym", icon: "💪", color: "#4CAF50" },
			worship: { label: "Place of Worship", icon: "🕌", color: "#FF9800" },
		}),
		[]
	);

	const barrierSpaces: SpaceMap = useMemo(
		() => ({
			foodDesert: { label: "Food Desert", icon: "🍽️", color: "#FF9800" },
			healthcareGap: { label: "Healthcare Gap", icon: "🏥", color: "#f44336" },
			transportBarrier: {
				label: "Transport Barrier",
				icon: "🚌",
				color: "#2196F3",
			},
			educationGap: { label: "Education Gap", icon: "🎓", color: "#4CAF50" },
			communityIsolation: {
				label: "Community Isolation",
				icon: "🤝",
				color: "#9C27B0",
			},
		}),
		[]
	);

	const showSpace = useCallback(
		(layerId: string) => {
			switch (layerId) {
				case "barriers":
					return barrierSpaces;
				case "personal-spaces":
					return personalSpaces;
				case "healthcare":
					return healthcareSpaces;
				case "food-nutrition":
					return foodSpaces;
				case "education":
					return educationSpaces;
				case "community":
					return communitySpaces;
				case "transportation":
					return transportationSpaces;
				case "housing":
					return housingSpaces;
				case "economic":
					return economicSpaces;
				default:
					return {};
			}
		},
		[
			barrierSpaces,
			personalSpaces,
			healthcareSpaces,
			foodSpaces,
			educationSpaces,
			communitySpaces,
			transportationSpaces,
			housingSpaces,
			economicSpaces,
		]
	);

	const toggleSpaceActive = useCallback(
		(space: string, spaceType: SpaceType) => {
			setActiveSpaces((prev) => ({
				...prev,
				[spaceType]: space,
			}));
		},
		[setActiveSpaces]
	);

	return {
		healthcareSpaces,
		foodSpaces,
		educationSpaces,
		communitySpaces,
		transportationSpaces,
		housingSpaces,
		economicSpaces,
		personalSpaces,
		barrierSpaces,
		activeSpaces,
		showSpace,
		toggleSpaceActive,
	};
};

export default useSpaces;
